import Image, { ImageLoaderProps } from 'next/image';
import styled, { CSSObject } from '@emotion/styled';
import { CSSProperties } from 'react';
import {
  applyLoaderTransforms,
  prepareBaseImageUrl,
} from '../../lib/mediaApiUrl';

const mediaApiImageLoader = ({ src, width, quality }: ImageLoaderProps) => {
  return applyLoaderTransforms({
    url: src,
    width,
    quality,
  });
};

const bypassImageLoader = ({ src }: ImageLoaderProps) => src;

type ImageFitType = 'crop' | 'scaleDown' | 'fill' | 'none';

const ImageWrapper = styled('div')<StyledImageProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 120px;
  position: relative;
  justify-self: center;

  ${({ imageCSSObject }) => imageCSSObject || ''}
`;

interface StyledImageProps {
  preventStretch?: boolean;
  imageCSSObject?: CSSObject;
  imagePositionRelative?: boolean;
  imageFit?: ImageFitType;
}
const shouldForwardProp = (prop: PropertyKey) => {
  const customProps: (keyof StyledImageProps)[] = [
    'preventStretch',
    'imageCSSObject',
    'imagePositionRelative',
    'imageFit',
  ];
  return !customProps.includes(prop as keyof StyledImageProps);
};

const StyledImage = styled(Image, { shouldForwardProp })<StyledImageProps>`
  flex: 1;
  justify-self: center;

  ${({ preventStretch }) => (preventStretch ? `height: auto !important;` : '')}

  ${({ imagePositionRelative }) =>
    imagePositionRelative ? 'position: relative !important;' : ''}

  ${({ imageFit }) => {
    switch (imageFit) {
      case 'crop':
        return 'object-fit: cover;';
      case 'fill':
        return 'object-fit: fill;';
      case 'none':
        return 'object-fit: none;';
      case 'scaleDown':
      default:
        return 'object-fit: contain;';
    }
  }}
`;

const prepUrl = (
  src: string,
  skipUrlProcessing: boolean,
  crop?: string | undefined,
  smartCrop?: string | undefined,
  rotation?: number | null | undefined
) => {
  const fixedSrc = src.startsWith('http') ? src : `https:${src}`;

  return skipUrlProcessing
    ? fixedSrc
    : prepareBaseImageUrl({
        url: fixedSrc,
        crop,
        smartCrop,
        rotation,
      });
};

export interface BaseNextImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  crop?: string | undefined;
  smartCrop?: string | undefined;
  rotation?: number | null | undefined;
  fill?: boolean;
  quality?: number;
  priority?: boolean;
  unoptimized?: boolean;
  loading?: 'lazy' | 'eager';
  imageFit?: ImageFitType;
  sizes?: string | undefined;
  id?: string;
  style?: CSSProperties | undefined;
  preventStretch?: boolean;
  imageCSSObject?: CSSObject | undefined;
  imagePositionRelative?: boolean;
  skipUrlProcessing?: boolean;
  dataTestId?: string;
  className?: string;
}

const BaseNextImage = ({
  src,
  alt,
  width,
  height,
  crop,
  smartCrop,
  rotation,
  fill = false,
  quality = 75,
  priority = false,
  unoptimized = false,
  loading = 'lazy',
  imageFit = 'scaleDown',
  sizes,
  id,
  style = {},
  className,
  preventStretch = false,
  imageCSSObject = {},
  imagePositionRelative = false,
  skipUrlProcessing = false,
  dataTestId = 'BaseNextImage',
}: BaseNextImageProps) => {
  if (!src) {
    return null;
  }

  const processedUrl = prepUrl(
    src,
    skipUrlProcessing,
    crop,
    smartCrop,
    rotation
  );

  const imageLoader = skipUrlProcessing
    ? bypassImageLoader
    : mediaApiImageLoader;

  return (
    <ImageWrapper imageCSSObject={imageCSSObject}>
      <StyledImage
        imageFit={imageFit}
        imagePositionRelative={imagePositionRelative}
        preventStretch={preventStretch}
        loader={imageLoader}
        src={processedUrl}
        alt={alt}
        {...(width ? { width } : {})}
        {...(height ? { height } : {})}
        {...(fill ? { fill } : {})} // Fill is required if width and height are not provided
        quality={quality}
        priority={priority}
        unoptimized={unoptimized}
        loading={loading}
        id={id}
        style={style}
        sizes={sizes}
        data-testid={dataTestId}
        className={className}
      />
    </ImageWrapper>
  );
};

export default BaseNextImage;
